/**
 * Default bindings to real browser / Electron-renderer APIs. Every function reads globals only
 * when called, so importing this module in Node (tests, SSR) is safe.
 */
import type { CaptureBackend, CaptureRecorder, CaptureStartInput } from './capture.js';
import type { AudioSink, DeviceLock, DeviceLockHandle, LifecyclePlatform, MediaPlatform, MediaStreamLike, MediaStreamTrackLike, PermissionWatch } from './platform.js';

// Compile-time proof that DOM objects satisfy the engine's structural types.
const asStream = (stream: MediaStream): MediaStreamLike => stream;
const asTrack = (track: MediaStreamTrack): MediaStreamTrackLike => track;
void asStream; void asTrack;

type SinkCapable = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/** Wraps a caller-owned or engine-owned audio element. The element need not be attached to the DOM. */
export function htmlAudioSink(element: HTMLAudioElement, owned = false): AudioSink {
  const el = element as SinkCapable;
  el.autoplay = true;
  return {
    setStream: stream => { el.srcObject = (stream as unknown as MediaStream | null); },
    setOutput: deviceId => (typeof el.setSinkId === 'function' ? el.setSinkId(deviceId) : Promise.reject(new Error('setSinkId unsupported'))),
    play: () => el.play(),
    dispose: () => {
      try { el.pause(); } catch { /* ignore */ }
      el.srcObject = null;
      if (owned) el.remove();
    },
  };
}

export function createBrowserMediaPlatform(): MediaPlatform {
  const nav = globalThis.navigator;
  const devices = nav?.mediaDevices;
  return {
    secureContext: globalThis.isSecureContext === true,
    webrtcSupported: typeof globalThis.RTCPeerConnection === 'function' && typeof devices?.getUserMedia === 'function',
    outputSelectionSupported: typeof globalThis.HTMLMediaElement === 'function' && 'setSinkId' in globalThis.HTMLMediaElement.prototype,
    getUserMedia: constraints => {
      if (!devices) return Promise.reject(Object.assign(new Error('mediaDevices unavailable'), { name: 'SecurityError' }));
      return devices.getUserMedia(constraints as MediaStreamConstraints);
    },
    cloneStream: stream => new MediaStream((stream as MediaStream).getAudioTracks().map(track => track.clone())),
    enumerateDevices: () => (devices ? devices.enumerateDevices() : Promise.resolve([])),
    onDeviceChange: callback => {
      if (!devices) return () => {};
      devices.addEventListener('devicechange', callback);
      return () => devices.removeEventListener('devicechange', callback);
    },
    watchMicrophonePermission: async (): Promise<PermissionWatch | null> => {
      try {
        const status = await nav.permissions.query({ name: 'microphone' as PermissionName });
        return {
          get state() { return status.state; },
          onChange: callback => {
            const handler = () => callback(status.state);
            status.addEventListener('change', handler);
            return () => status.removeEventListener('change', handler);
          },
        };
      } catch {
        return null;
      }
    },
    createAudioSink: () => htmlAudioSink(new Audio(), true),
  };
}

export function createBrowserLifecycle(): LifecyclePlatform {
  const listen = (target: EventTarget | undefined, type: string, callback: () => void) => {
    if (!target) return () => {};
    target.addEventListener(type, callback);
    return () => target.removeEventListener(type, callback);
  };
  return {
    isOnline: () => globalThis.navigator?.onLine !== false,
    onOnline: callback => listen(globalThis.window, 'online', callback),
    onOffline: callback => listen(globalThis.window, 'offline', callback),
    onVisible: callback => listen(globalThis.document, 'visibilitychange', () => { if (globalThis.document.visibilityState === 'visible') callback(); }),
  };
}

/** One phone per browser profile via the Web Locks API; undefined when unsupported. */
export function createWebLocksDeviceLock(name = 'bcio-voice-phone-device'): DeviceLock | undefined {
  const locks = globalThis.navigator?.locks;
  if (!locks) return undefined;
  return {
    // Never forcibly steal: callers coordinate an idle release, and a crashed page releases in the UA.
    acquire: () => new Promise<DeviceLockHandle | null>(resolve => {
      let release!: () => void;
      let markLost!: () => void;
      const held = new Promise<void>(r => { release = r; });
      const lost = new Promise<void>(r => { markLost = r; });
      locks.request(name, { ifAvailable: true }, async lock => {
        if (!lock) { resolve(null); return; }
        resolve({ lost, release });
        await held;
      }).catch(() => { markLost(); resolve(null); });
    }),
  };
}

/**
 * Stereo local capture through Web Audio + MediaRecorder. Channel 0: local microphone; channel 1:
 * remote audio. Not exercised in this repository's tests (Node has no Web Audio); see F2-REPORT.
 */
export function createBrowserCaptureBackend(): CaptureBackend {
  const mimeType = 'audio/webm;codecs=opus';
  const supported = typeof globalThis.AudioContext === 'function' && typeof globalThis.MediaRecorder === 'function'
    && typeof globalThis.MediaRecorder.isTypeSupported === 'function' && globalThis.MediaRecorder.isTypeSupported(mimeType);
  return {
    supported,
    async start(input: CaptureStartInput): Promise<CaptureRecorder> {
      const ctx = new AudioContext();
      const merger = ctx.createChannelMerger(2);
      const mono = (node: AudioNode) => {
        const gain = ctx.createGain();
        gain.channelCount = 1;
        gain.channelCountMode = 'explicit';
        gain.channelInterpretation = 'speakers';
        node.connect(gain);
        return gain;
      };
      let localSource = ctx.createMediaStreamSource(input.local as unknown as MediaStream);
      let localMono = mono(localSource);
      localMono.connect(merger, 0, 0);
      const remoteSource = ctx.createMediaStreamSource(input.remote as unknown as MediaStream);
      mono(remoteSource).connect(merger, 0, 1);
      const destination = ctx.createMediaStreamDestination();
      destination.channelCount = 2;
      merger.connect(destination);
      const recorder = new MediaRecorder(destination.stream, { mimeType, audioBitsPerSecond: 64_000 });
      recorder.ondataavailable = event => { if (event.data.size > 0) input.onData(event.data); };
      recorder.onerror = () => input.onError('recorder_error');
      recorder.start(input.timesliceMs);
      if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
      return {
        mimeType,
        replaceLocal(stream) {
          localMono.disconnect();
          localSource.disconnect();
          localSource = ctx.createMediaStreamSource(stream as unknown as MediaStream);
          localMono = mono(localSource);
          localMono.connect(merger, 0, 0);
        },
        async stop() {
          await new Promise<void>(resolve => {
            if (recorder.state === 'inactive') { resolve(); return; }
            recorder.addEventListener('stop', () => resolve(), { once: true });
            recorder.stop();
          });
          for (const track of destination.stream.getTracks()) track.stop();
          await ctx.close().catch(() => {});
        },
      };
    },
  };
}
